import { getErrorOrUnknown } from 'astra-core/utils/errors'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import {
  eventsProviderActions,
  MAX_PAGE_SIZE
} from 'astra-core/containers/EventsProvider'
import {
  ActiveEventStatus,
  EventsApiGetEventCountersRequest
} from 'betweb-openapi-axios'
import { isFeatureFlagEnabledSaga } from 'astra-core/containers/ConfigProvider/saga'
import { EFeatureFlags } from 'astra-core/containers/ConfigProvider/types'
import { selectCurrentLangCode } from 'astra-core/containers/LanguagesProvider'
import { DefaultCountersReq } from 'astra-core/containers/SportCountersProvider/constants'
import {
  getEventCounters,
  GetEventCountersResponse
} from 'astra-core/api/events/api'

import { eventsProviderSideEffects } from 'shared/lib/eventsProviderSideEffects'
import { getItemLocalStorage } from 'shared/lib/@system'
import { getFetchLineReqData } from 'pages/page-line/LineContainer/utils'

import {
  LINE_TOURNAMENT_FILTER_LS,
  lineTournamentsContainerActions
} from './slice'
import {
  FetchLinePayload,
  FetchLineTournamentsEventCountersPayload,
  FetchLineTournamentsPayload,
  TFormatLineTournamentsFiltersParams
} from './types'
import { selectLineTournamentsFilters } from './selectors'
import {
  formatLineTournamentsFiltersData,
  getFetchLineTournamentsReqData
} from './utils'

const SOURCE_KEY = 'lineTournaments'
const SOURCE_KEY_LINE_EVENTS = 'lineEvents'

const fetchLineSort = ['scheduledAt.asc']
const fetchLineStatuses = [
  'TRADING',
  'SUSPENDED',
  'PLANNING',
  'OPEN'
] as unknown as ActiveEventStatus[]

function* fetchLineTournamentsEventsCountersSaga(
  action: PayloadAction<FetchLineTournamentsEventCountersPayload>
) {
  try {
    const isGenericSportsEnabled: boolean = yield call(
      isFeatureFlagEnabledSaga,
      EFeatureFlags.GENERIC_SPORT_ENABLED
    )
    const locale: ReturnType<typeof selectCurrentLangCode> = yield select(
      selectCurrentLangCode
    )
    const request: EventsApiGetEventCountersRequest = {
      ...DefaultCountersReq.LINE,
      locale,
      hasLineOutcomes: true,
      includeSubsports: isGenericSportsEnabled,
      aggregateByGeneric: isGenericSportsEnabled,
      ...action.payload
    }

    const { data }: GetEventCountersResponse = yield call(
      getEventCounters,
      request
    )

    yield put(
      lineTournamentsContainerActions.fetchLineTournamentsEventCountersSuccess({
        counters: data.byTournament
      })
    )
  } catch (e) {
    const error = getErrorOrUnknown(e)
    yield put(
      lineTournamentsContainerActions.fetchLineTournamentsEventCountersError(
        error
      )
    )
  }
}

export function* fetchLineTournamentsSaga(
  action: PayloadAction<FetchLineTournamentsPayload>
) {
  const { sportId } = action.payload
  const filters = yield select(selectLineTournamentsFilters)

  const savedLSFilter = getItemLocalStorage(
    LINE_TOURNAMENT_FILTER_LS
  ) as TFormatLineTournamentsFiltersParams

  // from LS we filter only by TOP
  const currentFilter = savedLSFilter
    ? { ...filters, isTop: savedLSFilter.isTop }
    : filters

  const lineTournamentsFilter = formatLineTournamentsFiltersData(currentFilter)

  const reqData = getFetchLineTournamentsReqData({
    sportId,
    ...lineTournamentsFilter
  })

  yield put(
    eventsProviderActions.fetchTournaments({
      reqData: {
        ...reqData,
        pageSize: MAX_PAGE_SIZE,
        eventLiveliness: false,
        eventStatuses: fetchLineStatuses
      },
      sideEffects: eventsProviderSideEffects[SOURCE_KEY],
      params: {}
    })
  )

  if (sportId) {
    yield put(
      lineTournamentsContainerActions.fetchLineTournamentsEventCounters({
        scheduledFrom: lineTournamentsFilter.filterPeriod.scheduledFrom,
        scheduledTo: lineTournamentsFilter.filterPeriod.scheduledTo,
        sportIds: [sportId]
      })
    )
  }
}

export function* fetchLineSaga(action: PayloadAction<FetchLinePayload>) {
  const { tournamentId } = action.payload.reqData
  const filters = yield select(selectLineTournamentsFilters)

  const savedLSFilter = getItemLocalStorage(
    LINE_TOURNAMENT_FILTER_LS
  ) as TFormatLineTournamentsFiltersParams

  // from LS we filter only by TOP
  const currentFilter = savedLSFilter
    ? { ...filters, isTop: savedLSFilter.isTop }
    : filters

  const lineTournamentsFilter = formatLineTournamentsFiltersData(currentFilter)

  const reqData = getFetchLineReqData({
    tournamentId: String(tournamentId),
    ...lineTournamentsFilter
  })

  yield put(
    eventsProviderActions.fetchEvents({
      reqData: {
        ...reqData,
        pageSize: MAX_PAGE_SIZE,
        sort: fetchLineSort,
        statuses: fetchLineStatuses,
        hasLineOutcomes: true
      },
      sideEffects: eventsProviderSideEffects[SOURCE_KEY_LINE_EVENTS],
      params: action.payload.params
    })
  )
}

export function* lineTournamentsContainerSaga() {
  yield all([
    takeLatest(
      lineTournamentsContainerActions.fetchLineTournaments.type,
      fetchLineTournamentsSaga
    ),
    takeLatest(lineTournamentsContainerActions.fetchLine.type, fetchLineSaga),
    takeLatest(
      lineTournamentsContainerActions.fetchLineTournamentsEventCounters.type,
      fetchLineTournamentsEventsCountersSaga
    )
  ])
}
